import React, { useState } from 'react';
import clsx from 'clsx';

// Import PNG icons
import ProtectionIcon from '@/assets/features/protection.png';
import ShippingIcon from '@/assets/features/shipping.png';
import ClaimsIcon from '@/assets/features/claims.png';
import FraudIcon from '@/assets/features/fraud.png';
import ReturnsIcon from '@/assets/features/returns.png';

import ProtectionIconActive from '@/assets/features/protectionActive.png';
import ShippingIconActive from '@/assets/features/shippingActive.png';
import ClaimsIconActive from '@/assets/features/claimsActive.png';
import FraudIconActive from '@/assets/features/fraudActive.png';
import ReturnsIconActive from '@/assets/features/returnsActive.png';

interface FeatureContent {
  title: string;
  description: string;
  rgb: string;
  content: {
    heading: string;
    subheading: string;
  };
  link: string;
}

interface RedirectButtonProps {
  hideFeature?: string;
}

const features: FeatureContent[] = [
  {
    title: 'Protection',
    description: 'Coverage for accidents & more',
    rgb: '0,216,255',
    content: {
      heading: 'Protect what matters most with Protega Product Protection.',
      subheading:
        'Boost revenue and brand loyalty by offering protection from product failure and accidental damage.',
    },
    link: '/protection#',
  },
  {
    title: 'Shipping',
    description: 'Protection for loss, damage & theft',
    rgb: '52,211,153',
    content: {
      heading: 'Make sure it gets there with Protega Shipping Protection.',
      subheading:
        'Cut operational costs and eliminate stress caused by porch pirates and damaged deliveries.',
    },
    link: '/shipping#',
  },
  {
    title: 'Claims',
    description: 'Automated claim support',
    rgb: '251,191,36',
    content: {
      heading: 'Nurture customer loyalty with a self-service claims experience.',
      subheading:
        'Manage all delivery and fulfillment errors with a branded customer experience configured to reduce fraud and nurture brand loyalty.',
    },
    link: '/claims#',
  },
  {
    title: 'Fraud',
    description: 'AI-powered fraud detection',
    rgb: '248,113,113',
    content: {
      heading: 'Let AI help you stop bad actors from impacting your bottom line.',
      subheading:
        'A powerful post-purchase fraud mitigation platform is built seamlessly into every step of the customer journey, stopping fraudsters in their tracks.',
    },
    link: '/fraud#',
  },
  {
    title: 'Returns',
    description: 'Seamless returns & exchanges',
    rgb: '192,132,252',
    content: {
      heading: 'Find cost savings through intelligent returns and exchanges.',
      subheading:
        'Offer a personalized, customer-centered returns policy that increases lifetime value while lowering costs.',
    },
    link: '/returns#',
  },
];

const getIcon = (title: string, isHovered: boolean): string => {
  if (isHovered) {
    switch (title) {
      case 'Protection':
        return ProtectionIconActive;
      case 'Shipping':
        return ShippingIconActive;
      case 'Claims':
        return ClaimsIconActive;
      case 'Fraud':
        return FraudIconActive;
      case 'Returns':
        return ReturnsIconActive;
    }
  }
  switch (title) {
    case 'Protection':
      return ProtectionIcon;
    case 'Shipping':
      return ShippingIcon;
    case 'Claims':
      return ClaimsIcon;
    case 'Fraud':
      return FraudIcon;
    case 'Returns':
      return ReturnsIcon;
    default:
      return ProtectionIcon;
  }
};

const RedirectButton: React.FC<RedirectButtonProps> = ({ hideFeature }) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const visibleFeatures = features.filter(
    (feature) =>
      !hideFeature ||
      feature.title.toLowerCase() !== hideFeature.toLowerCase()
  );

  const firstRow = visibleFeatures.slice(0, 2);
  const secondRow = visibleFeatures.slice(2);

  const renderButton = (
    feature: FeatureContent,
    idx: number
  ): React.ReactNode => {
    const isActive = idx === hoverIndex;
    const glowRGB = feature.rgb;
    const iconSrc = getIcon(feature.title, isActive);

    return (
      <button
        key={feature.title}
        onClick={() => {
          window.location.href = feature.link;
        }}
        onMouseEnter={() => setHoverIndex(idx)}
        onMouseLeave={() => setHoverIndex(null)}
        className="tw-flex tw-items-center tw-w-full tw-transition-all tw-duration-200 tw-text-left"
        style={{
          backgroundColor: isActive
            ? `rgba(${glowRGB},1)`
            : 'transparent',
          borderRadius: isActive ? 24 : 16,
          boxShadow: isActive
            ? `0 0 40px rgba(${glowRGB},0.9)`
            : undefined,
          padding: '12px 24px',
          color: isActive ? '#000' : '#fff',
        }}
      >
        <img
          src={iconSrc}
          alt={feature.title}
          className="tw-h-10 tw-mr-4"
        />
        <div>
          <div
            className={clsx(
              'tw-font-semibold tw-text-lg',
              isActive ? 'tw-text-black' : 'tw-text-white'
            )}
          >
            {feature.title}
          </div>
          <div
            className={clsx(
              'tw-text-sm',
              isActive ? 'tw-text-black/90' : 'tw-text-gray-300'
            )}
          >
            {feature.description}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="tw-min-h-screen tw-bg-black tw-text-white tw-py-20 tw-px-6">
      <div className="tw-max-w-7xl tw-mx-auto">
        <div className="tw-text-center tw-mb-16">
          <h2 className="tw-text-xl md:tw-text-4xl tw-font-bold tw-mb-8">
            A platform built to boost your business.
          </h2>
        </div>

        {visibleFeatures.length === 5 ? (
          <div className="tw-max-w-5xl tw-mx-auto">
            <div className="tw-grid tw-grid-cols-2  tw-gap-6">
              {firstRow.map((f, i) => renderButton(f, i))}
            </div>
            <div className="tw-grid md:tw-grid-cols-3 tw-grid-cols-1 tw-gap-6 tw-mt-6">
              {secondRow.map((f, i) => renderButton(f, i + 2))}
            </div>
          </div>
        ) : (
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-2 tw-gap-6 tw-max-w-4xl tw-mx-auto">
            {visibleFeatures.map((f, i) => renderButton(f, i))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RedirectButton;
